import { VehicleType, VehicleTypeDisplayName } from '@transferz/models';
import React from 'react';

import { authorisedClient } from '../apollo/authorisedClient';
import { USER_BY_ID } from '../apollo/typeDefs/user';
import { BOOKING, JOURNEY } from '../queries/booking/bookingTypeDefs';
import { DRIVER } from '../queries/driver/driverTypeDefs';
import { FLEET } from '../queries/fleet/fleetTypeDefs';
import { HUB_INFORMATION, HUB_VEHICLE_CONFIGURATION } from '../queries/hub/hubTypeDefs';
import { BILLABLE_JOURNEY, INVOICE } from '../queries/invoice/invoiceTypeDefs';
import { ORGANIZATION } from '../queries/organization/organizationTypeDefs';
import { PARTNER } from '../queries/partner/partnerTypeDefs';
import { TRANSFER_COMPANY_DETAILS } from '../queries/transferCompany/transferCompanyTypeDefs';
import { VEHICLE } from '../queries/vehicle/vehicleTypeDefs';

interface Route {
  path: string;
  name: string | ((params: any) => Promise<any>);
  exact?: boolean;
  component: React.LazyExoticComponent<React.ComponentType<any>>;
}

interface Routes {
  [key: string]: Route;
}

const Dashboard = React.lazy(() => import('../routes/dashboard/Dashboard'));
const Profile = React.lazy(() => import('../routes/profile/Profile'));
const Users = React.lazy(() => import('../routes/user/users/Users'));
const UserDetails = React.lazy(() => import('../routes/user/userDetails/UserDetails'));
const Organizations = React.lazy(() => import('../routes/organization/organizations/Organizations'));
const OrganizationDetails = React.lazy(() => import('../routes/organization/organizationDetails/OrganizationDetails'));
const Hubs = React.lazy(() => import('../routes/hub/hubs/Hubs'));
const HubDetails = React.lazy(() => import('../routes/hub/hubDetails/HubDetails'));
const HubVehicleConfigurationDetails = React.lazy(() => import('../routes/hubVehicleConfigurationDetails/HubVehicleConfigurationDetails'));
const HubZipCodeGroupDetails = React.lazy(() => import('../routes/hub/hubZipCodeGroupDetails/HubZipCodeGroupDetails'));
const ExchangeRates = React.lazy(() => import('../routes/exchangeRates/exchangeRatesList/ExchangeRates'));
const ServiceBlockMain = React.lazy(() => import('../routes/serviceBlockMain/ServiceBlockMain'));
const TransferCompanyMain = React.lazy(() => import('../routes/transferCompanyMain/TransferCompanyMain'));
const TransferCompanyDetails = React.lazy(() => import('../routes/transferCompanyDetails/TransferCompanyDetails'));
const FleetDetails = React.lazy(() => import('../routes/fleetDetails/FleetDetails'));
const DriverDetails = React.lazy(() => import('../routes/driverDetails/DriverDetails'));
const PartnerMain = React.lazy(() => import('../routes/partnerMain/PartnerMain'));
const PartnerDetails = React.lazy(() => import('../routes/partnerDetails/PartnerDetails'));
const HubTenderDetails = React.lazy(() => import('../routes/hubTenderDetails/HubTenderDetails'));
const VehicleDetails = React.lazy(() => import('../routes/vehicleDetails/VehicleDetails'));
const TransferCompanyInvoiceMain = React.lazy(() => import('../routes/transferCompanyInvoiceMain/TransferCompanyInvoiceMain'));
const TransferCompanyInvoiceDetails = React.lazy(() => import('../routes/transferCompanyInvoiceDetails/TransferCompanyInvoiceDetails'));
const PartnerInvoiceDetails = React.lazy(() => import('../routes/partnerInvoiceDetails/PartnerInvoiceDetails'));
const PartnerInvoiceMain = React.lazy(() => import('../routes/partnerInvoiceMain/PartnerInvoiceMain'));
const BookingMain = React.lazy(() => import('../routes/bookingMain/BookingMain'));
const BookingDetails = React.lazy(() => import('../routes/bookingDetails/BookingDetails'));
const QuoteRequestDetails = React.lazy(() => import('../routes/quoteRequestDetails/QuoteRequestDetails'));
const BookingCreation = React.lazy(() => import('../routes/bookingCreation/BookingCreation'));
const JourneyDetails = React.lazy(() => import('../routes/journeyDetails/JourneyDetails'));
const PartnerGeoZoneMain = React.lazy(() => import('../routes/partnerGeoZoneMain/PartnerGeoZoneMain'));
const LogMain = React.lazy(() => import('../routes/logMain/LogMain'));
const SentEmails = React.lazy(() => import('../routes/sentEmails/SentEmails'));
const BillableJourneyMain = React.lazy(() => import('../routes/billableJourneyMain/BillableJourneyMain'));
const BillableJourneyDetails = React.lazy(() => import('../routes/billableJourneyDetails/BillableJourneyDetails'));
const JourneyMain = React.lazy(() => import('../routes/journeyMain/JourneyMain'));
const PendingJourneyMain = React.lazy(() => import('../routes/pendingJourneyMain/PendingJourneyMain'));
const ManualAdjustmentMain = React.lazy(() => import('../routes/manualAdjustmentMain/ManualAdjustmentMain'));
const UploadReviewFile = React.lazy(() => import('../routes/uploadReviewFile/UploadReviewFile'));
const ReviewFileDetails = React.lazy(() => import('../routes/reviewFileDetails/ReviewFileDetails'));
const ProcessReviewFileDetails = React.lazy(() => import('../routes/processReviewFileDetails/ProcessReviewFileDetails'));
const MaintenanceWindow = React.lazy(() => import('../routes/maintenanceWindow/MaintenanceWindow'));
const AwaitingApprovalRoute = React.lazy(() => import('../routes/awaitingApprovalRoute/AwaitingApprovalRoute'));
const QuoteRequestsMain = React.lazy(() => import('../routes/quoteRequests/QuoteRequestsMain'));
const WebhookEventsMain = React.lazy(() => import('../routes/webhookEvents/WebhookEventsMain'));
const AwaitingApprovalJourneyTaskPageMain = React.lazy(() => import('../routes/awaitingApprovalTaskPage/AwaitingApprovalTaskPageMain'));

export const routeConfig: Routes = {
  dashboard: {
    path: '/',
    name: 'dashboard',
    exact: true,
    component: Dashboard,
  },
  profile: {
    path: '/profile',
    name: 'profile',
    component: Profile,
  },
  users: {
    path: '/users',
    name: 'users',
    exact: true,
    component: Users,
  },
  userDetails: {
    path: '/users/:id',
    name: ({ id }) => {
      return authorisedClient.query({ query: USER_BY_ID, variables: { id: Number(id) } })
        .then(({ data: { user } }: any) => {
          return user.name;
        });
    },
    component: UserDetails,
  },
  organizations: {
    path: '/organizations',
    name: 'organizations',
    exact: true,
    component: Organizations,
  },
  organizationDetails: {
    path: '/organizations/:id',
    name: ({ id }) => {
      return authorisedClient.query({ query: ORGANIZATION, variables: { id: Number(id) } })
        .then(({ data: { organization } }: any) => {
          return organization.name;
        });
    },
    component: OrganizationDetails,
  },
  hubs: {
    path: '/hubs',
    name: 'hubs',
    exact: true,
    component: Hubs,
  },
  serviceBlockMain: {
    path: '/service-blocks',
    name: 'serviceBlocks',
    exact: true,
    component: ServiceBlockMain,
  },
  hubDetails: {
    path: '/hubs/:id',
    name: ({ id }) => {
      return authorisedClient.query({ query: HUB_INFORMATION, variables: { id: Number(id) } })
        .then(({ data: { hub } }: any) => {
          return hub.name;
        });
    },
    exact: true,
    component: HubDetails,
  },
  vehicleDetails: {
    path: '/hubs/:hubId/vehicle/:vehicleId',
    name: ({ vehicleId }) => {
      return authorisedClient.query({ query: HUB_VEHICLE_CONFIGURATION, variables: { id: Number(vehicleId) } })
        .then(({ data: { hubVehicleConfiguration } }: any) => {
          return VehicleTypeDisplayName[hubVehicleConfiguration.vehicleCategory as VehicleType];
        });
    },
    exact: true,
    component: HubVehicleConfigurationDetails,
  },
  zipCodeGroupDetails: {
    path: '/hubs/:hubId/zip-code-groups/:zipCodeGroupId',
    name: 'zipCodeGroupDetails',
    exact: true,
    component: HubZipCodeGroupDetails,
  },
  exchangeRates: {
    path: '/exchange-rates',
    name: 'exchangeRates',
    component: ExchangeRates,
  },
  transferCompanies: {
    path: '/transfer-companies',
    name: 'transferCompanies',
    exact: true,
    component: TransferCompanyMain,
  },
  transferCompanyDetails: {
    path: '/transfer-companies/:id',
    name: ({ id }) => {
      return authorisedClient.query({ query: TRANSFER_COMPANY_DETAILS, variables: { id: Number(id) } })
        .then(({ data: { transferCompany } }: any) => {
          return transferCompany.organization.name;
        });
    },
    exact: true,
    component: TransferCompanyDetails,
  },
  fleetDetails: {
    path: '/transfer-companies/:id/fleets/:fleetId',
    name: ({ fleetId }) => {
      return authorisedClient.query({ query: FLEET, variables: { fleetId: Number(fleetId) } })
        .then(({ data: { fleet } }: any) => {
          return fleet.name;
        });
    },
    exact: true,
    component: FleetDetails,
  },
  driverDetails: {
    path: '/transfer-companies/:id/fleets/:fleetId/drivers/:driverId',
    name: ({ driverId }) => {
      return authorisedClient.query({ query: DRIVER, variables: { driverId: Number(driverId) } })
        .then(({ data: { driver } }: any) => {
          return driver.name;
        });
    },
    exact: true,
    component: DriverDetails,
  },
  partners: {
    path: '/partners',
    name: 'partners',
    exact: true,
    component: PartnerMain,
  },
  partnerDetails: {
    path: '/partners/:id',
    name: ({ id }) => {
      return authorisedClient.query({ query: PARTNER, variables: { id: Number(id) } })
        .then(({ data: { partner } }: any) => {
          return partner.name;
        });
    },
    exact: true,
    component: PartnerDetails,
  },
  reviewFileHeaders: {
    path: '/review-file/:id',
    name: 'reviewFileDetails',
    exact: true,
    component: ReviewFileDetails,
  },
  processReviewFileDetails: {
    path: '/review-file/:id/process',
    name: 'processReviewFileDetails',
    exact: true,
    component: ProcessReviewFileDetails,
  },
  hubTenderDetails: {
    path: '/transfer-companies/:id/fleets/:fleetId/hubs/:hubTenderId',
    name: 'hubAssociationDetails',
    exact: true,
    component: HubTenderDetails,
  },
  transferCompanyVehicleDetails: {
    path: '/transfer-companies/:id/fleets/:fleetId/vehicle/:vehicleId',
    name: ({ vehicleId }) => {
      return authorisedClient.query({ query: VEHICLE, variables: { id: Number(vehicleId) } })
        .then(({ data: { vehicle } }: any) => {
          const categories = vehicle.categories
            .map((category: string) => VehicleTypeDisplayName[category as VehicleType])
            .join(' | ');
          return `${categories} - ${vehicle.licensePlate}`;
        });
    },
    exact: true,
    component: VehicleDetails,
  },
  transferCompanyInvoiceMain: {
    path: '/transfer-company-invoices',
    name: 'transferCompanyInvoices',
    exact: true,
    component: TransferCompanyInvoiceMain,
  },
  transferCompanyInvoiceDetails: {
    path: '/transfer-company-invoices/:id',
    name: ({ id }) => {
      return authorisedClient.query({ query: INVOICE, variables: { id: Number(id) } })
        .then(({ data: { invoice } }: any) => {
          return invoice.invoiceNumber;
        });
    },
    exact: true,
    component: TransferCompanyInvoiceDetails,
  },
  partnerInvoiceDetails: {
    path: '/partner-invoices/:id',
    name: ({ id }) => {
      return authorisedClient.query({ query: INVOICE, variables: { id: Number(id) } })
        .then(({ data: { invoice } }: any) => {
          return invoice.invoiceNumber;
        });
    },
    exact: true,
    component: PartnerInvoiceDetails,
  },
  partnerInvoiceMain: {
    path: '/partner-invoices',
    name: 'partnerInvoices',
    exact: true,
    component: PartnerInvoiceMain,
  },
  bookingMain: {
    path: '/bookings',
    name: 'bookings',
    exact: true,
    component: BookingMain,
  },
  bookingDetails: {
    path: '/bookings/:id',
    name: ({ id }) => {
      return authorisedClient.query({ query: BOOKING, variables: { id: Number(id) } })
        .then(({ data: { booking } }: any) => {
          return `Booking ${booking.code}`;
        });
    },
    exact: true,
    component: BookingDetails,
  },
  quoteRequestDetails: {
    path: '/quotes-requests/inbound/:inboundId/(outbound)?/:outboundId?/partner/:partnerId',
    name: 'quotes',
    exact: true,
    component: QuoteRequestDetails,
  },
  bookingCreation: {
    path: '/quote',
    name: 'quoteRequest',
    exact: true,
    component: BookingCreation,
  },
  journeyDetails: {
    path: '/bookings/:id/journeys/:journeyId',
    name: ({ journeyId }) => {
      return authorisedClient.query({ query: JOURNEY, variables: { id: Number(journeyId), includeCancellationDetails: true, refreshComplaints: true, includePriceSummary: true } })
        .then(({ data: { journey } }: any) => {
          return `Journey ${journey.journeyCode}`;
        });
    },
    exact: true,
    component: JourneyDetails,
  },
  geoZoneMain: {
    path: '/geo-zones',
    name: 'partnerGeoZones',
    exact: true,
    component: PartnerGeoZoneMain,
  },
  logMain: {
    path: '/logs',
    name: 'logs',
    exact: true,
    component: LogMain,
  },
  sentEmails: {
    path: '/sent-emails',
    name: 'sentEmails',
    exact: true,
    component: SentEmails,
  },
  billableJourneyMain: {
    path: '/billable-journeys',
    name: 'billableJourneys',
    exact: true,
    component: BillableJourneyMain,
  },
  billableJourneyDetails: {
    path: '/billable-journeys/:id',
    name: ({ id }) => {
      return authorisedClient.query({ query: BILLABLE_JOURNEY, variables: { id: Number(id) } })
        .then(({ data: { billableJourney } }: any) => {
          return billableJourney.id;
        });
    },
    exact: true,
    component: BillableJourneyDetails,
  },
  journeyMain: {
    path: '/journeys',
    name: 'journeyMain',
    exact: true,
    component: JourneyMain,
  },
  pendingJourneyMain: {
    path: '/pending-journeys',
    name: 'pendingJourneyMain',
    exact: true,
    component: PendingJourneyMain,
  },
  journey: {
    path: '/journeys/:journeyId',
    name: 'journeyDetails',
    exact: true,
    component: JourneyDetails,
  },
  manualAdjustmentMain: {
    path: '/manual-adjustments',
    name: 'manualAdjustments',
    exact: true,
    component: ManualAdjustmentMain,
  },
  reviewFile: {
    path: '/review-file',
    name: 'reviewFile',
    exact: true,
    component: UploadReviewFile,
  },
  maintenanceWindow: {
    path: '/maintenance-window',
    name: 'maintenanceWindow',
    exact: true,
    component: MaintenanceWindow,
  },
  awaitingApprovalJourneys: {
    path: '/awaiting-approvals',
    name: 'awaitingApprovalJourneys',
    exact: true,
    component: AwaitingApprovalRoute,
  },
  quoteRequests: {
    path: '/quote-requests',
    name: 'quoteRequests',
    exact: true,
    component: QuoteRequestsMain,
  },
  webhookEvents: {
    path: '/webhook-events',
    name: 'webhookEvents',
    exact: true,
    component: WebhookEventsMain,
  },
  awaitingApprovalJourneyTaskPage: {
    path: '/awaiting-approvals/:journeyId',
    name: 'journeyDetails',
    exact: true,
    component: AwaitingApprovalJourneyTaskPageMain,
  },
};

export const getRoutes = (): Route[] => {
  const paths: Route[] = [];

  Object.keys(routeConfig)
    .forEach(key => paths.push(routeConfig[key]));

  return paths;
};
